import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadTournamentsData } from '../services/publicTournamentService';

const ChallengeList = ({
  searchQuery,
  searchFields,
  searchMode = 'all',
  dateRange = null,
  sortOption,
  showSortDropdown,
  setShowSortDropdown,
  handleSortChange,
  onDataLoad
}) => {
  const [questions, setQuestions] = useState([]);
  const [filteredPackets, setFilteredPackets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ packets: [] }); // Store loaded data
  const navigate = useNavigate();

  // Load data from Supabase on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        console.log('Fetching tournaments from Supabase...');
        
        const tournamentsData = await loadTournamentsData();
        setData(tournamentsData);
        
        // Extract all questions from packets
        const allQuestions = [];

        if (tournamentsData.packets && Array.isArray(tournamentsData.packets)) {
          tournamentsData.packets.forEach(packet => {
            if (packet.questions_data && Array.isArray(packet.questions_data)) {
              packet.questions_data.forEach((question, index) => {
                allQuestions.push({
                  ...question,
                  packetTitle: packet.title,
                  packetId: packet.id,
                  packetDate: packet.date,
                  questionIndex: index
                });
              });
            }
          });
        }

        setQuestions(allQuestions);
        setLoading(false);
        console.log(`✓ Loaded ${tournamentsData.packets.length} tournaments, ${allQuestions.length} questions`);
        
      } catch (error) {
        console.error("Error loading questions data:", error);
        setError(error.message);
        setLoading(false);
        setQuestions([]);
        setData({ packets: [] });
      }
    };

    loadData();
  }, []); // Run once on mount

  // Parse European date format (dd.mm.yyyy)
  const parseEuropeanDate = (dateString) => {
    if (!dateString) return null;
    
    // Try to match dd.mm.yyyy or dd/mm/yyyy or dd-mm-yyyy
    const euroPattern = /\b(\d{1,2})[\.\/\-](\d{1,2})[\.\/\-](\d{2,4})\b/;
    const match = dateString.match(euroPattern);
    
    if (match) {
      const day = parseInt(match[1]);
      const month = parseInt(match[2]);
      let year = parseInt(match[3]);
      
      // Handle 2-digit years
      if (year < 100) {
        year = year >= 90 ? 1900 + year : 2000 + year;
      }
      
      // Validate date components
      if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
        return { year, month, day };
      }
    }
    
    // Fallback: try to extract just year and month from text
    const yearMatch = dateString.match(/\b(19|20)\d{2}\b/);
    const monthNames = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ];
    const lowerDate = dateString.toLowerCase();
    
    let year = null;
    let month = null;
    
    if (yearMatch) {
      year = parseInt(yearMatch[0]);
    }
    
    for (let i = 0; i < monthNames.length; i++) {
      if (lowerDate.includes(monthNames[i]) || lowerDate.includes(monthNames[i].slice(0, 3))) {
        month = i + 1;
        break;
      }
    }
    
    if (year || month) {
      return { year, month: month || null, day: null };
    }
    
    return null;
  };

  // Check if a parsed date is within the selected range
  const isDateInRange = (parsedDate, dateRange) => {
    if (!parsedDate || !parsedDate.year) return false;
    
    const dateValue = parsedDate.year * 12 + (parsedDate.month || 6);
    const minValue = dateRange.min * 12 + dateRange.minMonth;
    const maxValue = dateRange.max * 12 + dateRange.maxMonth;
    
    return dateValue >= minValue && dateValue <= maxValue;
  };

  // First, filter packets by date range if enabled
  useEffect(() => {
    if (dateRange && data.packets) {
      const packetsInRange = data.packets.filter(packet => {
        const parsedDate = parseEuropeanDate(packet.date);
        return isDateInRange(parsedDate, dateRange);
      });
      setFilteredPackets(packetsInRange);
    } else {
      setFilteredPackets(data.packets || []);
    }
  }, [dateRange, data.packets]);

  const getFilteredResults = () => {
    // If no search query, show filtered packets
    if (!searchQuery || !searchQuery.trim()) {
      return filteredPackets.map(packet => ({
        type: 'packet',
        id: packet.id,
        title: packet.title,
        author: Array.isArray(packet.author) ? packet.author.join(', ') : (packet.author || 'N/A'),
        date: packet.date,
        questions: packet.questions || packet.questions_data?.length || 0
      }));
    }

    // For search queries, only search within questions from date-filtered packets
    const searchTerms = searchQuery
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter(term => term.length > 0);

    if (searchTerms.length === 0) {
      return [];
    }

    // Get packet IDs that are in date range
    const allowedPacketIds = new Set(filteredPackets.map(p => p.id));

    return questions.filter(question => {
      // First check if this question's packet is in the allowed date range
      if (dateRange && !allowedPacketIds.has(question.packetId)) {
        return false;
      }

      // Build searchable text based on selected fields
      let searchableText = '';
      if (searchFields.questions) searchableText += ' ' + (question.text || '');
      if (searchFields.authors) searchableText += ' ' + (question.author || '');
      if (searchFields.answers) searchableText += ' ' + (question.answer || '');
      if (searchFields.championship) searchableText += ' ' + (question.packetTitle || '');
      if (searchFields.date) {
        searchableText += ' ' + (question.packetDate || '');
        searchableText += ' ' + (question.source || '');
      }
      searchableText = searchableText.toLowerCase();

      // Apply search mode (all words or any word)
      if (searchMode === 'all') {
        return searchTerms.every(term => searchableText.includes(term));
      } else {
        return searchTerms.some(term => searchableText.includes(term));
      }
    }).map(question => ({ type: 'question', ...question }));
  };

  const filteredResults = getFilteredResults();
  const isShowingPackets = !searchQuery || !searchQuery.trim();

  const truncateText = (text, maxLength = 100) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleRowClick = (result) => {
    if (result.type === 'packet') {
      navigateToPacket(result.id);
    } else {
      navigateToQuestion(result.packetId, result.questionIndex);
    }
  };

  const navigateToPacket = (packetId) => {
    navigate(`/packet/${packetId}`);
  };

  const navigateToQuestion = (packetId, questionIndex) => {
    const questionNumber = questionIndex + 1;
    navigate(`/packet/${packetId}/question/${questionNumber}`);
  };

  const getHighlightedText = (text, maxLength = 100) => {
    if (!text) return 'N/A';
    
    const truncated = text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    
    if (!searchQuery || !searchQuery.trim()) {
      return truncated;
    }
    
    const searchTerms = searchQuery
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter(term => term.length > 0);
    
    if (searchTerms.length === 0) return truncated;
    
    const pattern = new RegExp(`(${searchTerms.join('|')})`, 'gi');
    const highlightedText = truncated.replace(pattern, '<mark class="search-highlight">$1</mark>');
    
    return <span dangerouslySetInnerHTML={{ __html: highlightedText }} />;
  };

  const formatDateRange = (dateRange) => {
    if (!dateRange) return '';
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const startMonth = monthNames[dateRange.minMonth - 1];
    const endMonth = monthNames[dateRange.maxMonth - 1];
    return `${startMonth} ${dateRange.min} - ${endMonth} ${dateRange.max}`;
  };

  // Calculate statistics for display
  const dateRangeStats = dateRange ? {
    totalPackets: data.packets?.length || 0,
    filteredPackets: filteredPackets.length,
    excludedPackets: (data.packets?.length || 0) - filteredPackets.length
  } : null;

  // Show error state
  if (error) {
    return (
      <div className="challenges-section">
        <div className="error-message" style={styles.error}>
          <h3>⚠️ შეცდომა</h3>
          <p>კითხვების ჩატვირთვა ვერ მოხერხდა: {error}</p>
          <p>გთხოვთ შეამოწმოთ ინტერნეტ კავშირი და სცადოთ თავიდან.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="challenges-section" style={styles.challengesSection}>
      <div className="content-header" style={styles.contentHeader}>
        <h3>{isShowingPackets ? 'პაკეტები' : 'კითხვების ძებნის შედეგები'}</h3>
        
        {/* Date filtering info */}
        {dateRange && (
          <div className="date-filter-info">
            <p className="filter-stats">
              თარიღის ფილტრი: {formatDateRange(dateRange)}
            </p>
            <p className="filter-stats">
              ნაჩვენებია {dateRangeStats.filteredPackets} პაკეტი {dateRangeStats.totalPackets}-დან
              {dateRangeStats.excludedPackets > 0 && (
                <span className="excluded-count">
                  {' '}({dateRangeStats.excludedPackets} გამორიცხულია)
                </span>
              )}
            </p>
          </div>
        )}

        {/* Search info */}
        {!isShowingPackets && (
          <div className="search-info-container">
            <p className="search-info" style={styles.searchInfo}>
              {searchMode === 'all' 
                ? `ყველა სიტყვით ძებნის შედეგი: "${searchQuery}"` 
                : `ნებისმიერი სიტყვით ძებნის შედეგი: "${searchQuery}"`}
              {dateRange && ` (${formatDateRange(dateRange)} პერიოდში)`}
            </p>
            <p className="search-info" style={styles.searchInfo}>
              ნაპოვნია {filteredResults.length} პასუხი{filteredResults.length !== 1 ? 's' : ''}
            </p>
            <p className="search-info" style={styles.searchInfo}>
              დააჭირეთ კითხვას მის გასახსნელად.
            </p>
          </div>
        )}
      </div>

      <div className="challenges-table-container" style={styles.tableContainer}>
        {loading ? (
          <div className="loading-message" style={styles.loading}>
            მიმდინარეობს კითხვების ჩატვირთვა Supabase-დან...
          </div>
        ) : (
          <table className="challenges-table" style={styles.table}>
            <thead>
              <tr>
                {isShowingPackets ? (
                  <>
                    <th style={styles.th}>სათაური</th>
                    <th style={styles.th}>ავტორი</th>
                    <th style={styles.th}>თარიღი</th>
                    <th style={styles.th}>კითხვები</th>
                  </>
                ) : (
                  <>
                    <th style={styles.th}>კითხვა</th>
                    <th style={styles.th}>ავტორი</th>
                    <th style={styles.th}>პაკეტი</th>
                    <th style={styles.th}>თარიღი</th>
                    <th style={styles.th}>პასუხი</th>
                    <th style={styles.th}>სურათი</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredResults.length > 0 ? (
                filteredResults.map((result) => (
                  <tr 
                    key={result.type === 'packet' ? `packet-${result.id}` : `${result.packetId}-${result.questionIndex}`}
                    className="clickable-row"
                    style={styles.clickableRow}
                    onClick={() => handleRowClick(result)}
                    title={result.type === 'packet' ? 
                      `Click to open packet: ${result.title}` : 
                      `Click to open question ${result.questionIndex + 1} from ${result.packetTitle}`
                    }
                  >
                    {result.type === 'packet' ? (
                      <>
                        <td style={styles.td} title={result.title}>
                          {truncateText(result.title, 60)}
                        </td>
                        <td style={styles.td}>{result.author}</td>
                        <td style={styles.td}>{result.date || 'N/A'}</td>
                        <td style={styles.td}>{result.questions || 0}</td>
                      </>
                    ) : (
                      <>
                        {/* Question text */}
                        <td style={styles.td} title={result.text}>
                          <div className="question-cell" style={styles.questionCell}>
                            <span className="question-number" style={styles.questionNumber}>Q{result.questionIndex + 1}:</span>
                            <span className="question-text">
                              {searchFields.questions 
                                ? getHighlightedText(result.text, 80) 
                                : truncateText(result.text, 80)}
                            </span>
                          </div>
                        </td>

                        {/* Author */}
                        <td style={styles.td}>
                          {searchFields.authors 
                            ? getHighlightedText(result.author || 'N/A', 30) 
                            : (result.author || 'N/A')}
                        </td>

                        {/* Packet */}
                        <td style={styles.td} title={result.packetTitle}>
                          {searchFields.championship 
                            ? getHighlightedText(result.packetTitle, 40) 
                            : truncateText(result.packetTitle, 40)}
                        </td>

                        {/* Date */}
                        <td style={styles.td}>{result.packetDate || 'N/A'}</td>

                        {/* Answer */}
                        <td style={styles.td}>
                          {searchFields.answers 
                            ? getHighlightedText(result.answer, 30) 
                            : truncateText(result.answer, 30)}
                        </td>

                        {/* Image indicator */}
                        <td style={styles.td} className="image-cell">
                          {result.image && result.image.src ? (
                            <span className="has-image" title={result.image.alt || 'Has image'}>
                              📷
                            </span>
                          ) : (
                            <span className="no-image">-</span>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={isShowingPackets ? 4 : 6} className="no-results-message" style={styles.noResults}>
                    {searchQuery && searchQuery.trim() ? (
                      `ვერ მოიძებნა კითხვები, რომლებიც შეესაბამება "${searchQuery}"
                      ${searchMode === 'all' ? ' (ყველა სიტყვით)' : ' (ნებისმიერი სიტყვით)'}
                      ${dateRange ? ` ${formatDateRange(dateRange)} პერიოდში` : ''}`
                    ) : dateRange && filteredPackets.length === 0 ? (
                      `ვერ მოიძებნა პაკეტები ${formatDateRange(dateRange)} პერიოდში`
                    ) : (
                      'პაკეტი ვერ მოიძებნა.'
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// Inline styles (matching your ChallengeList.css)
const styles = {
  challengesSection: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box',
  },
  contentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.1rem',
  },
  tableContainer: {
    backgroundColor: '#1e1e1e',
    borderRadius: '0.5rem',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '0.75rem 1rem',
    color: '#a0aec0',
    fontSize: '0.875rem',
    borderBottom: '1px solid #4a5568',
    fontWeight: 500,
  },
  td: {
    padding: '.8rem',
    width: '0.054vh',
    justifyContent: 'center',
    borderBottom: '1px solid #4a5568',
    fontSize: '1rem',
  },
  clickableRow: {
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },
  questionCell: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  questionNumber: {
    fontWeight: 'bold',
    color: '#4facfe',
    minWidth: '30px',
    flexShrink: 0,
  },
  searchInfo: {
    fontSize: '0.9em',
    color: '#666',
    marginTop: '5px',
    fontStyle: 'italic',
  },
  loading: {
    padding: '1.5rem',
    textAlign: 'center',
    color: '#a0aec0',
  },
  noResults: {
    padding: '1.5rem',
    textAlign: 'center',
    color: '#a0aec0',
  },
  error: {
    padding: '2rem',
    textAlign: 'center',
    background: '#fff5f5',
    border: '1px solid #feb2b2',
    borderRadius: '0.5rem',
    color: '#c53030',
  },
};

export default ChallengeList;